import { useAsyncData } from 'nuxt/app';
import {
  computed,
  readonly,
  toValue,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';
import { useApiModel } from '~/composables/useApiModel';
import type { Candidate, Chamber } from '~/types/Candidates';
import type { ApiError, ApiMedia } from '~/types/plugins/api';
import { mapSourceLink, type ApiSourceLike } from '~/utils/mapSourceLink';

// Shared raw shapes for the Show Candidate response — reused by
// useCandidateCampaigns/useCandidateLobbyists since there's no dedicated
// `/candidates/{id}/campaigns` or `/candidates/{id}/lobbyists` endpoint;
// all three composables read their own slice out of the one response fetched
// by `useCandidateDetail` below.
export interface ApiCandidateDetailParty {
  id: number;
  name: string;
  slug: string;
}

export interface ApiCandidateDetailElectorate {
  id: number;
  name: string;
}

export interface ApiCandidateDetailCampaign {
  id: number;
  title: string;
  slug: string;
  status: 'active' | 'inactive';
  type: 'community' | 'party';
  description: string | null;
  /**
   * Whether the candidate is linked to this campaign directly or inherits it
   * from their party (`CandidateResource::mergeBySource()`). Optional because
   * the marker is only added where the two sets are merged.
   */
  association_source?: 'direct' | 'inherited';
}

export interface ApiCandidateDetailLobbyist {
  id: number;
  name: string;
  slug: string;
  industry: string | null;
  description?: string | null;
  website?: string | null;
  logo: ApiMedia | null;
}

export interface ApiCandidateDetail {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  slug: string;
  position: Chamber;
  biography: string | null;
  is_incumbent: boolean;
  image: ApiMedia | null;
  website: string | null;
  // Always null on the show endpoint — whenCounted is never triggered here.
  campaigns_count: number | null;
  lobbyists_count: number | null;
  party?: ApiCandidateDetailParty | null;
  electorate?: ApiCandidateDetailElectorate | null;
  campaigns?: ApiCandidateDetailCampaign[];
  lobbyists?: ApiCandidateDetailLobbyist[];
  sources?: ApiSourceLike[];
}

export function mapCandidateDetail(raw: ApiCandidateDetail): Candidate {
  return {
    id: raw.slug,
    name: raw.full_name,
    initials:
      `${raw.first_name.charAt(0)}${raw.last_name.charAt(0)}`.toUpperCase(),
    image: raw.image?.thumb_url ?? raw.image?.url ?? null,
    party: raw.party?.name ?? null,
    partySlug: raw.party?.slug ?? null,
    incumbent: raw.is_incumbent,
    chamber: raw.position,
    electorate: raw.electorate?.name ?? '',
    bio: raw.biography ?? '',
    website: raw.website ?? '',
    // `campaigns_count`/`lobbyists_count` are always null on this endpoint —
    // derive from the eager-loaded arrays instead.
    campaignsCount: raw.campaigns?.length ?? 0,
    lobbyistsCount: raw.lobbyists?.length ?? 0,
    sources: (raw.sources ?? []).map(mapSourceLink),
  };
}

// Fetches the raw Show Candidate response once per candidate and shares it
// across every consumer that keys on the same id — useCandidate,
// useCandidateCampaigns and useCandidateLobbyists all call this instead of
// each independently calling `.find(id)`.
//
// `useApiModel`'s request deduplication does NOT collapse those three calls:
// it is gated on `import.meta.server` (see useApiModel.ts) and returns
// `requestFn()` directly on the server, i.e. it's a no-op precisely where SSR
// needs it. `useAsyncData`'s own key-based dedup is what actually collapses
// them — the same mechanism useElection's `ELECTION_KEY` relies on for its
// two simultaneous banner mounts. It's also what lets Nuxt await the fetch on
// the server and hand the client the result in the payload, instead of
// rendering `candidate: null` and refetching after hydration (VTW-574-style).
export function useCandidateDetail(id: MaybeRefOrGetter<string>) {
  const candidateId = toValue(id);

  return useAsyncData<ApiCandidateDetail | null>(
    `candidate-${candidateId}`,
    async () => {
      const model = useApiModel<ApiCandidateDetail>('candidates', {
        autoLoad: false,
      });

      return (await model.find(candidateId)) ?? null;
    },
    { default: () => null },
  );
}

export function useCandidate(id: MaybeRefOrGetter<string>) {
  const { data, error, status } = useCandidateDetail(id);

  const candidate = computed(() =>
    data.value ? mapCandidateDetail(data.value) : null,
  );

  return {
    candidate: readonly(candidate) as Readonly<Ref<Candidate | null>>,
    pending: computed(() => status.value === 'pending'),
    error: readonly(error) as Readonly<Ref<ApiError | null>>,
  };
}
